import { HttpParams } from "@angular/common/http";
import { Component, forwardRef, Inject, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MatButtonToggleChange } from "@angular/material/button-toggle";
import { DateAdapter } from "@angular/material/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import * as moment from "moment";
import * as moment_tz from 'moment-timezone';
import { firstValueFrom, take } from "rxjs";
import { ErrorMessages, Permissions } from "../../common/constants";
import { isEmpty } from "../../common/helper";
import { isOnline } from "../../common/setup";
import { ActionDefinitionSparePartReference, ActionDiscardReason, ActionExecutionState, ActionIssueType, ActionState, ActionStatus, ActionStatusAttachment, ActivationPeriod, ActivationType, AlertDefinition, Customer, Location, Metric, ProductModelSparePartDefinitionReference, SparePartDefinition, StoreOrderItem, StoreOrderItemType, Thing, WorkOrder, WorkOrderStatus } from "../../model";
import { WearStatus } from "../../model/wear-status";
import { ActionDiscardReasonService } from "../../service/action-discard-reason.service";
import { ActionIssueTypeService } from "../../service/action-issue-type.service";
import { ActionStatusAttachmentService } from "../../service/action-status-attachment.service";
import { AlertDefinitionService } from "../../service/alert-definition.service";
import { AuthenticationService } from "../../service/authentication.service";
import { ContextService } from "../../service/context.service";
import { CustomPropertyService } from "../../service/custom-property.service";
import { HttpService } from "../../service/http.service";
import { MetricService } from "../../service/metric.service";
import { ProductModelSparePartDefinitionReferenceService } from "../../service/product-model-spare-part-definition-reference.service";
import { StoreCartService } from "../../service/store-cart.service";
import { UserActionStatusService } from "../../service/user-action-status.service";
import { WearStatusService } from "../../service/wear-status.service";
import { AbstractThingContextService } from "../../shared/class/abstract-thing-context-service.class";
import { MessageComponent } from "../../shared/component";
import { ConfirmDialog } from "../../shared/confirm-dialog/confirm-dialog.component";
import { CustomPropertyFormComponent } from "../../shared/custom-property/custom-property-form.component";
import { LocalizationPipe } from "../../shared/pipe";
import { StatusTagPipeBody } from "../../shared/pipe/status-tag.pipe";
import { HttpUtility } from "../../utility";
import { ErrorUtility } from "../../utility/error-utility";
import { SparePartInputComponent } from "./spare-part-input.component";

@Component({
    selector: 'action-info-dialog-v2',
    template: require('./action-info-dialog-v2.component.html'),
    styles: [require('./action-info-dialog-v2.component.css')]
})
export class ActionInfoDialogV2Component {

    @ViewChild('saveMessage') saveMessage: MessageComponent;

    @ViewChild('noteForm') noteForm: NgForm;

    @ViewChild('detailsForm') detailsForm: NgForm;

    @ViewChild('customPropForm') customPropForm: CustomPropertyFormComponent;

    @ViewChild(SparePartInputComponent) sparePartInput: SparePartInputComponent;

    writePermission: boolean;
    wearMetric: Metric;
    actionStatus: ActionStatus;
    sparePartDefinitionMap: { [id: string]: { sparePartDefinition: SparePartDefinition, quantity: number } };
    showConfirmationForm: boolean;
    updating: boolean;
    error: string;
    activeForDuration: string;
    confirmReplacementEnabled: boolean;
    addToCartEnabled: boolean;
    loaded: boolean;
    showWearDetails: boolean;
    isHistorical: boolean;
    customer: Customer;
    location: Location;
    thing: Thing;
    showExceedStandardUsageMessage: boolean;
    statusTagPipeBody: StatusTagPipeBody = { icon: null, iconStyle: null, color: '#7C0505', backgroundColor: '#F80A0A26' };
    initialExecutionState: ActionExecutionState;
    selectedExecutionState: ActionExecutionState;
    insideWorkOrder: boolean;
    canUpdateState: boolean;
    issueTypes: ActionIssueType[];
    actionDiscardReasons: ActionDiscardReason[];
    mandatoryDiscardReason: boolean;
    detailsProperties: { label: string, value: string }[] = [];
    showCustomProperties: boolean;
    showSparePartInput: boolean;
    actionAttachments: ActionStatusAttachment[];
    executionStartDate: moment.Moment;
    executionDate: moment.Moment;
    executionStartTime: string;
    executionTime: string;
    estimatedWorkTime: number;
    recurrenceTagPipeBody: StatusTagPipeBody = { icon: null, iconStyle: null, color: '#212121', backgroundColor: '#42424226' };
    recurrenceTitle: string;
    recurrencePercentage: number = 0;
    recurrenceThresholdExceeded: boolean;
    recurrenceValue: number;
    recurrenceThreshold: number;
    recurrenceTotal: string;
    recurrenceRemainingInfo: string;
    locale: string;
    digitOptions = { minimumFractionDigits: 0, maximumFractionDigits: 1 };

    private saveBody: any;
    private refreshActionList: boolean;
    private mandatoryAttachmentPropertyNames: string[];
    private workOrderContext: WorkOrder;
    private timezone: string;

    static RECURRENCE_TITLE = "Every ${value}";
    static RECURRENCE_REMAINING_INFO = "${value} left";

    constructor(
        @Inject(forwardRef(() => StoreCartService)) private storeCartService: StoreCartService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => UserActionStatusService)) private userActionStatusService: UserActionStatusService,
        @Inject(forwardRef(() => AlertDefinitionService)) private alertDefinitionService: AlertDefinitionService,
        @Inject(forwardRef(() => WearStatusService)) private wearStatusService: WearStatusService,
        @Inject(forwardRef(() => ProductModelSparePartDefinitionReferenceService)) private productModelSparePartDefinitionReferenceService: ProductModelSparePartDefinitionReferenceService,
        @Inject(forwardRef(() => MatDialogRef)) public dialogRef: MatDialogRef<ActionInfoDialogV2Component>,
        @Inject(MAT_DIALOG_DATA) data,
        @Inject(forwardRef(() => MetricService)) private metricService: MetricService,
        @Inject(forwardRef(() => ActionIssueTypeService)) private actionIssueTypeService: ActionIssueTypeService,
        @Inject(forwardRef(() => ActionDiscardReasonService)) private actionDiscardReasonService: ActionDiscardReasonService,
        @Inject(forwardRef(() => CustomPropertyService)) private customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog,
        @Inject(forwardRef(() => ActionStatusAttachmentService)) private actionStatusAttachmentService: ActionStatusAttachmentService,
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => HttpUtility)) private httpUtility: HttpUtility,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService,
        @Inject(forwardRef(() => DateAdapter)) private dateAdapter: DateAdapter<moment.Moment>,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService
    ) {
        this.isHistorical = data.isHistorical;
        this.wearMetric = data.wearMetric;
        this.actionStatus = data.actionStatus;
        this.computeWritePermission();
        this.estimatedWorkTime = this.getEstimatedWorkTime();
        this.actionAttachments = this.actionStatus.attachments;
        this.workOrderContext = data.workOrderContext;
        this.extractContextObjects();
        this.sparePartDefinitionMap = data.sparePartDefinitionMap;
        this.activeForDuration = this.getActiveForDuration();
        this.addToCartEnabled = this.storeCartService.canAddToCart() && !this.isHistorical;
        if (!this.wearMetric) {
            this.retrieveSparePartDefinition();
        } else {
            this.loaded = true;
        }
        this.initialExecutionState = this.isHistorical ? (this.actionStatus as any).state : (this.actionStatus.executionState || null);
        this.selectedExecutionState = this.initialExecutionState;
        this.insideWorkOrder = !!this.actionStatus.workOrderId;
        this.canUpdateState = !this.insideWorkOrder || this.isHistorical || ((this.actionStatus.workOrder || this.workOrderContext).status == WorkOrderStatus.EXECUTION && this.authenticationService.hasPermission(Permissions.WRITE_WORK_ORDER));
        this.showSparePartInput = this.authenticationService.getTenant().uncodedActionSparePartsEnabled && this.actionStatus.uncoded;
        this.loadActionIssueTypes();
        this.loadActionDiscardReasons();
        this.mandatoryDiscardReason = this.actionStatus.actionDefinition?.mandatoryDiscardReason;
        this.handleActionProperties();
        this.timezone = this.contextService.getTimezone();
        this.initDates();
        this.locale = this.authenticationService.getLocale();
        this.dateAdapter.setLocale(this.locale);
        this.initRecurrenceValues();
    }

    private computeWritePermission(): void {
        if (this.actionStatus.actionDefinition?.userTypeFiltered) {
            const user = this.authenticationService.getUser();
            this.writePermission = this.actionStatus.actionDefinition.writeUserTypeIds?.includes(user.userTypeId);
        } else {
            this.writePermission = true;
        }
    }

    private getEstimatedWorkTime(): number {
        const workTime = this.actionStatus.workTime || this.actionStatus.actionDefinition?.workTime;
        return workTime > 0 ? workTime : null;
    }

    private initDates(): void {
        if (this.isHistorical) {
            if (this.actionStatus.executionStartTimestamp) {
                this.executionStartDate = moment_tz.tz(this.actionStatus.executionStartTimestamp, this.timezone);
                this.executionStartTime = this.getTimeString(this.executionStartDate);
            }
            if (this.actionStatus.executionTimestamp) {
                this.executionDate = moment_tz.tz(this.actionStatus.executionTimestamp, this.timezone);
                this.executionTime = this.getTimeString(this.executionDate);
            }
        } else {
            this.executionStartDate = moment_tz.tz(this.timezone);
            this.executionStartTime = this.getTimeString(this.executionStartDate);
            this.executionDate = moment_tz.tz(this.timezone);
            if (this.actionStatus.workTime) {
                this.executionDate.add(this.actionStatus.workTime, 'minutes');
            }
            this.executionTime = this.getTimeString(this.executionDate);
        }
    }

    private extractContextObjects(): void {
        this.thing = this.actionStatus.thing || ({} as Thing);
        this.thing.id = this.actionStatus.thingId;
        if (this.actionStatus.location && this.actionStatus.locationId) {
            this.location = this.actionStatus.location;
            this.location.id = this.actionStatus.locationId;
        }
        if (this.actionStatus.customer && this.actionStatus.customerId) {
            this.customer = this.actionStatus.customer;
            this.customer.id = this.actionStatus.customerId;
        }
    }

    private retrieveSparePartDefinition(): void {
        if (this.actionStatus?.actionDefinition?.alertDefinitionIds?.length) {
            let promises = [];
            this.actionStatus.actionDefinition.alertDefinitionIds.forEach(id => promises.push(this.alertDefinitionService.getAlertDefinitionById(id)));
            Promise.all(promises).then((alertDefinitions: AlertDefinition[]) => {
                const wearMetricId = alertDefinitions.find(ad => ad.activeConditionWearMetricId)?.activeConditionWearMetricId;
                if (wearMetricId) {
                    firstValueFrom(this.metricService.getMetricById(wearMetricId)).then(wearMetric => {
                        if (wearMetric) {
                            this.wearMetric = wearMetric;
                            this.showWearDetails = true;
                            this.getWearStatus(wearMetricId).then(wearStatus => {
                                if (wearStatus?.productModelSparePartDefinitionReferenceIds?.length) {
                                    this.getProductModelSparePartDefinitionReferences(wearStatus.productModelSparePartDefinitionReferenceIds).then(references => {
                                        this.buildSparePartDefinitionMap(references);
                                        this.loaded = true;
                                    }).catch(err => this.handleError(err));
                                } else {
                                    this.loaded = true;
                                }
                            });
                        }
                    }).catch(err => this.handleError(err));
                } else {
                    this.loaded = true;
                }
            }).catch(err => this.handleError(err));
        } else {
            this.loaded = true;
        }
    }

    private getWearStatus(wearMetricId: string): Promise<WearStatus> {
        if (this.isHistorical) {
            return Promise.resolve(this.actionStatus.wearStatus);
        } else {
            return this.wearStatusService.getWearStatusesFromThingIdAndMetricId(this.thing.id, wearMetricId).then(results => {
                return results?.length ? results[0] : null;
            });
        }
    }

    private handleError(err): void {
        this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
    }

    private getProductModelSparePartDefinitionReferences(referenceIds: string[]): Promise<any> {
        let params = new HttpParams();
        referenceIds.forEach(id => params = params.append('productModelSparePartDefinitionReferenceId', id));
        return this.productModelSparePartDefinitionReferenceService.getRecursivelyAllProductModelSparePartDefinitionReferences(null, null, params);
    }

    private buildSparePartDefinitionMap(references: ProductModelSparePartDefinitionReference[]): void {
        if (references?.length) {
            this.sparePartDefinitionMap = {};
            references?.forEach(ref => {
                const oldQuantity: number = this.sparePartDefinitionMap[ref.sparePartDefinitionId] ? this.sparePartDefinitionMap[ref.sparePartDefinitionId].quantity : 0;
                this.sparePartDefinitionMap[ref.sparePartDefinitionId] = { sparePartDefinition: ref.sparePartDefinition, quantity: (oldQuantity + ref.quantity) };
            });
        }
    }

    private getActiveForDuration(): string {
        let duration = moment.duration((this.isHistorical ? this.actionStatus.executionTimestamp : new Date().getTime()) - this.actionStatus.startTimestamp);
        const numberOfDays = Math.max(Math.floor(duration.asDays()), 0);  // avoid negative
        return (numberOfDays > 0 ? numberOfDays : '< 1') + " " + this.localizationPipe.transform("days");
    }

    private loadActionIssueTypes(): void {
        this.actionIssueTypeService.getAllIssueTypes().then(types => {
            this.issueTypes = types;
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
    }

    private loadActionDiscardReasons(): void {
        this.actionDiscardReasonService.getAllActionDiscardReasons().then(reasons => {
            this.actionDiscardReasons = reasons;
            if (this.isHistorical) {
                this.actionDiscardReasons.push({ id: 'wo_discarded', code: 'wo_discarded', label: 'Work Order discarded' });
            }
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
    }

    private getSparePartDefinitionStoreItem(sparePartDefinitionId: string): StoreOrderItem {
        let newItem = new StoreOrderItem();
        newItem.itemId = sparePartDefinitionId;
        newItem.type = StoreOrderItemType.SPARE_PART;
        newItem.quantity = this.sparePartDefinitionMap[sparePartDefinitionId].quantity;
        newItem.thingId = this.thing.id;
        return newItem;
    }

    addToCart(): void {
        let newItems: StoreOrderItem[] = [];
        this.updating = true;
        Object.keys(this.sparePartDefinitionMap).forEach(key => {
            newItems.push(this.getSparePartDefinitionStoreItem(key));
        });
        this.storeCartService.addItemToCart(newItems).then(() => {
            this.updating = false;
            this.saveMessage.show();
            this.error = null;
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.SAVE_DATA_ERROR));
    }


    back(): void {
        this.showConfirmationForm = false;
        this.saveBody = null;
    }

    confirmReplacement(): void {
        this.doSave();
    }

    isOverflow(el: HTMLElement): boolean {
        var isOverflowing = el.clientWidth < el.scrollWidth
            || el.clientHeight < el.scrollHeight;
        return isOverflowing;
    }

    close(): void {
        this.dialogRef.close(this.refreshActionList);
    }


    updateShowExceedStandardUsageMessage(value: boolean): void {
        this.showExceedStandardUsageMessage = value;
    }

    toggleChangeExecutionState($event: MatButtonToggleChange) {
        const toggle = $event.source;
        if (toggle && $event.value.some(item => item == toggle.value)) {
            toggle.buttonToggleGroup.value = [toggle.value];
            this.selectedExecutionState = toggle.value;
        } else {
            this.selectedExecutionState = null;
        }
    }

    save(): void {
        this.saveBody = this.getSaveBody();
        if (this.selectedExecutionState?.startsWith('DONE') && this.wearMetric && !this.isHistorical) {
            this.showConfirmationForm = true;
        } else {
            this.doSave();
        }
    }

    private doSave(): void {
        this.updating = true;
        this.userActionStatusService.patchActionStatus(this.actionStatus, this.saveBody, this.isHistorical).then(() => {
            this.updating = false;
            this.dialogRef.close(true);
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.SAVE_DATA_ERROR));
    }

    private getSaveBody(): any {
        const formValue = this.noteForm?.form.getRawValue();
        const detailsFormValue = this.detailsForm?.form.getRawValue();
        let detailsBody = {
            executionStartTimestamp: detailsFormValue?.executionStartDate ? this.getTimestamp(detailsFormValue.executionStartDate, detailsFormValue.executionStartTime) : null,
            executionTimestamp: detailsFormValue?.executionDate ? this.getTimestamp(detailsFormValue.executionDate, detailsFormValue.executionTime) : null
        };
        let body = Object.assign({}, formValue, detailsBody);
        if (this.insideWorkOrder || this.isHistorical) {
            body.executionState = this.selectedExecutionState;
        } else if (this.selectedExecutionState) {
            body.state = this.selectedExecutionState == ActionExecutionState.DISCARDED ? ActionState.DISCARDED : ActionState.DONE;
        }
        body.properties = this.customPropForm?.getProperties();
        if (this.sparePartInput) {
            let sparePartReferences: ActionDefinitionSparePartReference[] = [];
            const ref: ActionDefinitionSparePartReference = this.sparePartInput.getValue();
            if (ref.code) {
                sparePartReferences.push(ref);
            }
            body.sparePartReferences = sparePartReferences;
        }
        return body;
    }

    private handleActionProperties(): void {
        this.showCustomProperties = this.customPropertyService.getEditableActionPropertyDefinitions(this.actionStatus, null).length > 0 || (this.showSparePartInput);
        const readOnlyPropDef = this.customPropertyService.getReadOnlyActionPropertyDefinitions(this.actionStatus.uncoded, this.actionStatus.actionDefinition?.propertyReferences);
        let result: { label: string, value: string }[] = [];
        readOnlyPropDef.forEach(propDef => {
            result.push({ label: propDef.label || propDef.name, value: !this.actionStatus.properties || isEmpty(this.actionStatus.properties[propDef.name]) ? '-' : this.actionStatus.properties[propDef.name].toString() });
        });
        this.detailsProperties = result;
        this.mandatoryAttachmentPropertyNames = this.customPropertyService.getEditableActionPropertyDefinitions(this.actionStatus, null).filter(prop => prop.mandatoryAttachment).map(p => { return p.name });
    };

    isCustomPropertiesFormValid(): boolean {
        if (!this.customPropForm) {
            return true;
        }
        return this.customPropForm.checkForm();
    }

    uploadAttachment(event): void {
        let attachment: File = null;
        const files: FileList = <any>(event.srcElement || event.target).files;
        if (files.length > 0) {
            attachment = files.item(0);
        } else {
            attachment = null;
        }
        if (attachment) {
            this.updating = true;
            const data = new FormData();
            data.set('file', attachment, attachment.name);
            this.actionStatusAttachmentService.uploadAttachment(this.actionStatus.id, data, this.isHistorical).then(() => {
                this.refreshActionList = true;
                this.refreshActionStatusAttachments();
            }).catch(err => {
                this.error = ErrorUtility.getMessage(err, ErrorMessages.SAVE_DATA_ERROR);
                this.updating = false;
            });
        }
    }

    downloadAttachment(attachment: ActionStatusAttachment) {
        firstValueFrom(this.httpService.externalGet(attachment.url, { responseType: 'blob' })).then(blob => {
            this.httpUtility.wrapFileAndDownload({ file: blob, fileName: attachment.filename });
        });
    }

    openDeleteAttachmentDialog(attachment: ActionStatusAttachment): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.panelClass = "action-delete-attachment-dialog";
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            title: "attachmentDeleteMessage",
            message: "attachmentDeleteInfoMessage"
        }
        this.dialog.open(ConfirmDialog, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                this.updating = true;
                this.actionStatusAttachmentService.deleteAttachment(this.actionStatus.id, attachment.s3Key, this.isHistorical).then(() => {
                    this.refreshActionList = true;
                    this.refreshActionStatusAttachments();
                }).catch(err => {
                    this.error = ErrorUtility.getMessage(err, ErrorMessages.DELETE_DATA_ERROR);
                    this.updating = false;
                });
            }
        });
    }

    private refreshActionStatusAttachments(): void {
        this.userActionStatusService.getActionStatusById(this.actionStatus.id, this.isHistorical).then(action => {
            this.actionAttachments = action.attachments;
            this.updating = false;
            this.error = null;
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
    }

    isAttachmentMandatory(): boolean {
        if (this.customPropForm) {
            const propertyValues = this.customPropForm.getProperties();
            if (propertyValues && this.mandatoryAttachmentPropertyNames?.some(propName => propertyValues[propName]?.toString() == 'true')) {
                return true;
            }
        }
        return false;
    }

    isOnline(): boolean {
        return isOnline();
    }

    private getTimeString(moment: moment.Moment): string {
        return this.formatString(moment.hours()) + ':' + this.formatString(moment.minutes());
    }

    private formatString(num: number) {
        return num.toString().length == 1 ? '0' + num : num;
    }

    private getTimestamp(date: moment.Moment, time: string) {
        const localeDate = moment_tz.tz(date.format("YYYY-MM-DD"), 'YYYY-MM-DD', this.timezone);
        let hours = Number(time?.substring(0, 2));
        let minutes = Number(time?.substring(3, 5));
        let result = localeDate.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
        return result.valueOf();
    }

    setExecutionStartNow(): void {
        const now = moment_tz.tz(this.timezone);
        this.detailsForm.form.controls['executionStartDate'].setValue(now);
        this.detailsForm.form.controls['executionStartTime'].setValue(this.getTimeString(now));
        this.detailsForm.form.markAsDirty();
    }

    setExecutionNow(): void {
        const now = moment_tz.tz(this.timezone);
        this.detailsForm.form.controls['executionDate'].setValue(now);
        this.detailsForm.form.controls['executionTime'].setValue(this.getTimeString(now));
        this.detailsForm.form.markAsDirty();
    }

    private initRecurrenceValues(): void {
        if (this.actionStatus.actionDefinition?.activationType == ActivationType.USAGE) {
            this.getUsageMetric(this.actionStatus.actionDefinition.usageMetricId).then(metric => {
                const recurrenceUnit = metric?.unit ? this.localizationPipe.transform(metric.unit) : null;
                this.recurrenceTitle = this.localizationPipe.transform(ActionInfoDialogV2Component.RECURRENCE_TITLE).replace('${value}', this.actionStatus.actionDefinition.usageThreshold + (recurrenceUnit ? (' ' + recurrenceUnit) : ''));
                this.recurrenceValue = this.actionStatus.usage != null ? this.actionStatus.usage : 0;
                this.recurrenceThreshold = this.actionStatus.actionDefinition.usageThreshold;
                this.recurrenceThresholdExceeded = this.recurrenceValue > (this.recurrenceThreshold);
                this.recurrencePercentage = this.recurrenceValue * (100 / (this.recurrenceThreshold));
                this.recurrenceTotal = this.recurrenceThreshold + (recurrenceUnit ? (' ' + recurrenceUnit) : '');
                if (!this.recurrenceThresholdExceeded) {
                    const totalRemaining = this.recurrenceThreshold - this.recurrenceValue;
                    const remainingInfo = this.localizationPipe.transform(ActionInfoDialogV2Component.RECURRENCE_REMAINING_INFO).replace('${value}', totalRemaining + (recurrenceUnit ? (' ' + recurrenceUnit) : ''));
                    this.recurrenceRemainingInfo = ' (' + remainingInfo + ')';
                }
            });
        } else if (this.actionStatus.actionDefinition?.activationType == ActivationType.PERIOD) {
            const period: ActivationPeriod = this.actionStatus.actionDefinition.period;
            const periodCount = this.actionStatus.actionDefinition.periodCount;
            let periodUnit;
            switch (period) {
                case ActivationPeriod.DAILY:
                    periodUnit = "day";
                    break;
                case ActivationPeriod.MONTHLY:
                    periodUnit = "month";
                    break;
                case ActivationPeriod.WEEKLY:
                    periodUnit = "week";
                    break;
            }
            if (periodCount > 1) {
                periodUnit += 's';
            }
            this.recurrenceTitle = this.localizationPipe.transform(ActionInfoDialogV2Component.RECURRENCE_TITLE).replace('${value}', periodCount + ' ' + this.localizationPipe.transform(periodUnit));
        }
    }

    private getUsageMetric(metricId: string): Promise<Metric> {
        if (metricId) {
            return firstValueFrom(this.metricService.getMetricById(metricId));
        } else {
            return Promise.resolve(null);
        }
    }

}